// Personal services set-up and its module-wide settings (PRD 121 §4.2–4.4, §6).
//
//   GET   /api/v1/business/appointments/settings                    every member
//   POST  /api/v1/business/appointments/settings/set-up             owner
//   PATCH /api/v1/business/appointments/settings                    owner; lock_version, 409 stale
//   POST  /api/v1/business/appointments/settings/attestations       owner; the insurance answer
//   GET   /api/v1/business/appointments/settings/slug-availability  owner; 30 a minute
//   PUT   /api/v1/business/appointments/settings/booking-slug       owner; at most 5 a year
//   GET   /api/v1/business/appointments/settings/booking-slugs      the address history
//
// Called by the Overview, the Set-up and Settings pages, and BusinessContext
// (the set-up state the sidebar and the Administration tile read through
// businessModulesStore).
//
// EVERY WRITE PARSES ITS RESPONSE. apiFetch never throws on a status, so a
// write that dropped the Response would turn 422 covered_entity_refused into a
// success — a clinic that bills insurance told it was set up. parseData throws
// BusinessApiError with the server's code, message and details instead.

import { businessApiGet, businessApiPatch, businessApiPost, businessApiPut } from '../utils/apiUtils';
import { BusinessApiError, buildQuery, parseData, parseEnvelope } from './businessApiCommon';
import type {
  BookingSlugAvailability,
  BookingSlugHistory,
  BookingSlugRow,
  PersonalServicesSettingsPayload,
  PersonalServicesSettingsUpdate,
  PersonalServicesSetUpParams,
} from '../types/personalServicesApi';

const SETTINGS_PATH = '/api/v1/business/appointments/settings';

/** A write before set-up (PersonalServices::Scoped). */
export const PERSONAL_SERVICES_NOT_SET_UP_CODE = 'personal_services_not_set_up';
/** Set-up refused because the business has never chosen a time zone. */
export const TIME_ZONE_REQUIRED_CODE = 'time_zone_required';
/** A yes to the insurance question: 422 at set-up, 403 on every later write (§4.3). */
export const COVERED_ENTITY_REFUSED_CODE = 'covered_entity_refused';
/** A save made against an older lock_version. */
export const STALE_CODE = 'stale';
/** The module gate's refusal: the business does not hold personal services. */
export const MODULE_REQUIRED_CODE = 'module_required';
/** The booking address's refusals (§4.4): field `booking_slug` at set-up, `slug` on a change. */
export const SLUG_ERROR_CODES = ['slug_invalid', 'slug_reserved', 'slug_taken'] as const;
/** More than five address changes in 365 days. */
export const SLUG_CHANGE_LIMIT_CODE = 'slug_change_limit';

export const personalServicesSettingsService = {
  /** Never creates anything: `set_up: false` and no `settings` before set-up. */
  async get(): Promise<PersonalServicesSettingsPayload> {
    return parseData<PersonalServicesSettingsPayload>(await businessApiGet(SETTINGS_PATH));
  },

  /**
   * The one explicit act that turns a business holding the module into one
   * using it. Idempotent on the api: a second call answers the existing row.
   * A yes to the insurance question answers 422 covered_entity_refused and
   * creates nothing but the answer itself.
   */
  async setUp(params: PersonalServicesSetUpParams): Promise<PersonalServicesSettingsPayload> {
    return parseData<PersonalServicesSettingsPayload>(await businessApiPost(`${SETTINGS_PATH}/set-up`, params));
  },

  /** Owner only; `lock_version` is the one last read. */
  async update(params: PersonalServicesSettingsUpdate): Promise<PersonalServicesSettingsPayload> {
    return parseData<PersonalServicesSettingsPayload>(await businessApiPatch(SETTINGS_PATH, params));
  },

  /** "Our answer has changed": a yes pauses the module; the owner's later no resumes it (Dave, 2026-10-22). */
  async recordAttestation(answer: boolean): Promise<PersonalServicesSettingsPayload> {
    return parseData<PersonalServicesSettingsPayload>(
      await businessApiPost(`${SETTINGS_PATH}/attestations`, { answer })
    );
  },

  /** The live check under the booking address field. Throttled on the api, so callers debounce. */
  async slugAvailability(slug: string): Promise<BookingSlugAvailability> {
    return parseData<BookingSlugAvailability>(
      await businessApiGet(`${SETTINGS_PATH}/slug-availability${buildQuery({ slug })}`)
    );
  },

  /** Retires the current address (it keeps redirecting) and claims this one. */
  async changeBookingSlug(slug: string): Promise<PersonalServicesSettingsPayload> {
    return parseData<PersonalServicesSettingsPayload>(
      await businessApiPut(`${SETTINGS_PATH}/booking-slug`, { slug })
    );
  },

  /** The rows are `data`; the allowance (`changes_left`, `next_change_at`) rides in `meta`. */
  async bookingSlugs(): Promise<BookingSlugHistory> {
    const { data, meta } = await parseEnvelope<BookingSlugRow[]>(await businessApiGet(`${SETTINGS_PATH}/booking-slugs`));
    return {
      booking_slugs: data,
      changes_left_this_year: typeof meta?.changes_left === 'number' ? meta.changes_left : 0,
      next_change_at: typeof meta?.next_change_at === 'string' ? meta.next_change_at : null,
    };
  },
};

/**
 * The settings as they now are, from a 409 `stale` refusal — the api sends
 * them in `details.current`. Null for any other error.
 */
export function staleSettingsOf(error: unknown): PersonalServicesSettingsPayload | null {
  if (!(error instanceof BusinessApiError) || error.code !== STALE_CODE) return null;
  const details = error.details as unknown as { current?: PersonalServicesSettingsPayload } | null;
  return details?.current ?? null;
}

/** The message a refusal pins on one field (`booking_slug`, `time_zone`), or null. */
export function fieldErrorOf(error: unknown, field: string): string | null {
  if (!(error instanceof BusinessApiError) || !Array.isArray(error.details)) return null;
  const detail = error.details.find(item => item?.field === field);
  return detail ? detail.message || error.message : null;
}

/** A booking address refusal (format, reserved, taken, or the yearly limit). */
export function isSlugError(error: unknown): error is BusinessApiError {
  return (
    error instanceof BusinessApiError &&
    ((SLUG_ERROR_CODES as readonly string[]).includes(error.code) || error.code === SLUG_CHANGE_LIMIT_CODE)
  );
}
